/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import {
  faCopy,
  faCheck,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faPlus,
  faLink,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { showToast } from "../components/toast";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { subMonths, startOfMonth } from "date-fns";
import moment from "moment";
import { PermissionWrapper } from "../helpers/permission";

const UtmLinksList = () => {
  const [linksList, setLinksList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);

  // 🏷️ Tab toggle: "streetgains" or "folio"
  const [activeTab, setActiveTab] = useState("streetgains");

  // 🔎 Search Filters
  const [shortUrlFilter, setShortUrlFilter] = useState("");
  const [pagenameFilter, setPagenameFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [mediumFilter, setMediumFilter] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // ↕️ Sorting States
  const [sortField, setSortField] = useState("created_on");
  const [sortOrder, setSortOrder] = useState("desc");

  // 📑 Backend Pagination States
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const navigate = useNavigate();
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // 🔄 Fetch data directly from backend with pagination & filters
  const fetchLinksData = useCallback(
    async (pageNumber = page) => {
      try {
        setLoading(true);

        const params = {
          stype: activeTab,
          page: pageNumber,
          limit: limit,
          orderbycol: sortField || "created_on",
          orderby: sortOrder.toUpperCase(),
        };

        if (shortUrlFilter.trim()) params.short_url = shortUrlFilter.trim();
        if (pagenameFilter.trim()) params.pagename = pagenameFilter.trim();
        if (sourceFilter.trim()) params.utm_source = sourceFilter.trim();
        if (campaignFilter.trim()) params.utm_campaign = campaignFilter.trim();
        if (mediumFilter.trim()) params.utm_medium = mediumFilter.trim();

        if (startDate) {
          params.from_date = moment(startDate).format("YYYY-MM-DD");
          params.to_date = endDate
            ? moment(endDate).format("YYYY-MM-DD")
            : moment(startDate).format("YYYY-MM-DD");
        }

        const response = await axios.get(`${API_URL}utm-links`, { params });

        if (response.data && response.data.success) {
          setLinksList(response.data.data || []);
          setTotal(response.data.total || 0);
          setPage(pageNumber);
        } else {
          setLinksList([]);
          setTotal(0);
        }
      } catch (error) {
        setLinksList([]);
        setTotal(0);
        const errorMessage =
          error.response?.data?.message || "Failed to fetch UTM links data";
        showToast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      activeTab,
      page,
      limit,
      sortField,
      sortOrder,
      shortUrlFilter,
      pagenameFilter,
      sourceFilter,
      campaignFilter,
      mediumFilter,
      startDate,
      endDate,
    ]
  );

  // Debounced fetch on filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLinksData(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [
    activeTab,
    sortField,
    sortOrder,
    shortUrlFilter,
    pagenameFilter,
    sourceFilter,
    campaignFilter,
    mediumFilter,
    startDate,
    endDate,
  ]);

  const toggleTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleClearFilters = () => {
    setShortUrlFilter("");
    setPagenameFilter("");
    setSourceFilter("");
    setCampaignFilter("");
    setMediumFilter("");
    setDateRange([null, null]);
  };

  // ↕️ Sort handler
  const sortBox = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // 📑 Pagination calculations from backend total
  const pageCount = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      fetchLinksData(newPage);
    }
  };

  const handleCopyLink = (url) => {
    if (!url) return;
    const fullUrl = url.startsWith("http") ? url : `https://${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(url);
    showToast.success("Copied to clipboard!");
    setTimeout(() => {
      setCopiedLink(null);
    }, 2000);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this UTM link!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}utm-links/${id}`);
        if (response.data && response.data.success) {
          Swal.fire("Deleted!", "Your UTM link has been deleted.", "success");
          fetchLinksData(page);
        }
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to delete UTM link.",
          "error"
        );
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Header section */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">
              <FontAwesomeIcon icon={faLink} className="me-2 text-primary" />{" "}
              Manage UTM Links
            </h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="utm_links" action="add">
              <Link
                to={`/utm-links/add?stype=${activeTab}`}
                className="btn btn-default btn-sm"
                style={{ marginRight: "8px" }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add UTM Link
              </Link>
            </PermissionWrapper>

            {/* Pagination Controls in Header (Backend Driven) */}
            {total > 0 && (
              <div className="d-inline-block ms-2">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount || pageCount === 0}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation - Exact standard CRM button group */}
        <div className="m-b-15">
          <div className="btn-group" role="group" aria-label="UTM Type Selection">
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === "streetgains" ? "btn-primary active" : "btn-default"
              }`}
              onClick={() => toggleTab("streetgains")}
              style={{
                padding: "8px 24px",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: activeTab === "streetgains" ? "#348fe2" : "#f1f3f5",
                color: activeTab === "streetgains" ? "#ffffff" : "#495057",
                borderColor: activeTab === "streetgains" ? "#348fe2" : "#ced4da",
                boxShadow:
                  activeTab === "streetgains"
                    ? "0 2px 4px rgba(52, 143, 226, 0.3)"
                    : "none",
                cursor: "pointer",
                zIndex: activeTab === "streetgains" ? 2 : 1,
                borderTopLeftRadius: "4px",
                borderBottomLeftRadius: "4px",
                borderTopRightRadius: "0px",
                borderBottomRightRadius: "0px",
                transition: "all 0.2s ease-in-out",
              }}
            >
              Streetgains
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === "folio" ? "btn-primary active" : "btn-default"
              }`}
              onClick={() => toggleTab("folio")}
              style={{
                padding: "8px 24px",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: activeTab === "folio" ? "#348fe2" : "#f1f3f5",
                color: activeTab === "folio" ? "#ffffff" : "#495057",
                borderColor: activeTab === "folio" ? "#348fe2" : "#ced4da",
                boxShadow:
                  activeTab === "folio"
                    ? "0 2px 4px rgba(52, 143, 226, 0.3)"
                    : "none",
                cursor: "pointer",
                zIndex: activeTab === "folio" ? 2 : 1,
                borderTopLeftRadius: "0px",
                borderBottomLeftRadius: "0px",
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                transition: "all 0.2s ease-in-out",
              }}
            >
              Streetfolio
            </button>
          </div>
        </div>

        {/* Panel with Table */}
        <div className="panel panel-default with-rounded-corner with-shadow">
          <style>{`
            .sticky-utm-table thead tr:first-child th {
              position: sticky;
              top: 0;
              z-index: 20;
              background: #f8f9fa;
            }
            .sticky-utm-table thead tr:nth-child(2) th {
              position: sticky;
              top: 30px;
              z-index: 19;
              background: white;
            }
            .sticky-table-container {
              max-height: calc(100vh - 190px);
              overflow: auto;
            }
            .sticky-utm-table th {
              vertical-align: top;
            }
            .skeleton-box {
              height: 14px;
              background: #f0f0f0;
              border-radius: 3px;
              animation: pulse 1.5s infinite;
            }
            @keyframes pulse {
              0% { opacity: 0.6; }
              50% { opacity: 1; }
              100% { opacity: 0.6; }
            }
            .sticky-utm-table thead tr:nth-child(2) input {
              font-size: 11px !important;
              height: 26px !important;
              padding: 2px 8px !important;
            }
            .sticky-utm-table thead tr:nth-child(2) input::placeholder {
              font-size: 11px !important;
              color: #999 !important;
            }
            .custom-datepicker-wrapper .react-datepicker-wrapper,
            .custom-datepicker-wrapper .react-datepicker__input-container {
              width: 100%;
            }
            .custom-datepicker-wrapper input {
              width: 100%;
              border: 1px solid #ccd0d4;
              border-radius: 3px;
            }
          `}</style>
          <div className="table-responsive sticky-table-container">
            <table className="table m-b-0 sticky-utm-table">
              <thead>
                <tr>
                  <th style={{ width: "50px", textAlign: "center" }}>#</th>
                  <th
                    style={{ minWidth: "180px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("short_url")}
                  >
                    Short Link <FontAwesomeIcon icon={getSortIcon("short_url")} />
                  </th>
                  <th
                    style={{ minWidth: "160px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("pagename")}
                  >
                    Page Name <FontAwesomeIcon icon={getSortIcon("pagename")} />
                  </th>
                  <th
                    style={{ minWidth: "150px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("utm_source")}
                  >
                    UTM Source <FontAwesomeIcon icon={getSortIcon("utm_source")} />
                  </th>
                  <th
                    style={{ minWidth: "160px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("utm_campaign")}
                  >
                    UTM Campaign <FontAwesomeIcon icon={getSortIcon("utm_campaign")} />
                  </th>
                  <th
                    style={{ minWidth: "150px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("utm_medium")}
                  >
                    UTM Medium <FontAwesomeIcon icon={getSortIcon("utm_medium")} />
                  </th>
                  <th
                    style={{ minWidth: "170px", cursor: "pointer", whiteSpace: "nowrap" }}
                    onClick={() => sortBox("created_on")}
                  >
                    Created On <FontAwesomeIcon icon={getSortIcon("created_on")} />
                  </th>
                  <th style={{ width: "95px", textAlign: "center" }}>Action</th>
                </tr>
                {/* Search Filter Row */}
                <tr>
                  <th style={{ textAlign: "center" }}></th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search link..."
                        value={shortUrlFilter}
                        onChange={(e) => setShortUrlFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search page..."
                        value={pagenameFilter}
                        onChange={(e) => setPagenameFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search source..."
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search campaign..."
                        value={campaignFilter}
                        onChange={(e) => setCampaignFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search medium..."
                        value={mediumFilter}
                        onChange={(e) => setMediumFilter(e.target.value)}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={startDate}
                        onChange={(dates) => setDateRange(dates)}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText="Select Date"
                        monthsShown={3}
                        openToDate={prevMonthStart}
                        dateFormat="dd-MM-yyyy"
                      />
                    </div>
                  </th>
                  <th style={{ textAlign: "center" }}>
                    <button
                      className="btn btn-default btn-xs"
                      onClick={handleClearFilters}
                      title="Clear filters"
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, rIndex) => (
                    <tr key={rIndex} className="skeleton-row">
                      {Array.from({ length: 8 }).map((_, cIndex) => (
                        <td key={cIndex}>
                          <div className="skeleton-box"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : linksList.length > 0 ? (
                  linksList.map((item, index) => {
                    const rowNum = (page - 1) * limit + index + 1;
                    const isCopied = copiedLink === item.short_url;

                    return (
                      <tr key={item.id || index}>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {rowNum}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.short_url}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.pagename}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>{item.utm_source}</td>
                        <td style={{ verticalAlign: "middle" }}>{item.utm_campaign}</td>
                        <td style={{ verticalAlign: "middle" }}>{item.utm_medium}</td>
                        <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                          {formatDate(item.created_on)}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <button
                            type="button"
                            className={`btn ${
                              isCopied ? "btn-success" : "btn-primary"
                            } btn-xs`}
                            onClick={() => handleCopyLink(item.short_url)}
                            title="Copy Short Link"
                            style={{ marginRight: "4px" }}
                          >
                            <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
                          </button>
                          <PermissionWrapper module="utm_links" action="delete">
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDelete(item.id)}
                              title="Delete Link"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center p-3">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UtmLinksList;
